import { FloppyDiskIcon } from '@phosphor-icons/react';
import { format } from 'date-fns/format';
import { FC, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { deepDifference } from '@/helpers';
import { PageHeader } from '@/modules/common';
import LoadingContainer from '@/modules/common/components/loading-container';
import { Routes } from '@/router/routes';

import { useOverlayDetails } from '../api';
import { OverlayForm } from '../components';
import { OverlayItem } from '../types';
import { OverlayFormValues } from '../validations';

export const OverlayDetailsPage: FC = () => {
  const params = useParams<{ id: string }>();

  const { details, uploadUrlDataMutation, isFetching, updateOverlayMutation } =
    useOverlayDetails(params.id);

  const defaultValues = useMemo(() => {
    if (!details) return null;

    return {
      ...details,
      updatedAt: format(new Date(details.updatedAt), 'yyyy-MM-dd'),
    };
  }, [details]);

  const updateOverlay = async (values: OverlayFormValues) => {
    try {
      const body = deepDifference<OverlayItem>(values, defaultValues);

      if (values.imagePublicUrl instanceof File) {
        await uploadUrlDataMutation.mutateAsync({
          file: values.imagePublicUrl,
        });
      }

      await updateOverlayMutation.mutateAsync({
        ...body,
        id: params.id,
      });
      toast.success(`Overlay “${values.name}” updated successfully!`);
    } catch (error) {
      console.error(error.message);
    }
  };

  return (
    <LoadingContainer loading={isFetching}>
      <OverlayForm
        editMode
        onSubmit={updateOverlay}
        defaultValues={defaultValues}
      >
        <PageHeader
          title={details?.name}
          asChildPage
          backPath={Routes.Overlays}
          actions={[
            <Button
              key="save-overlay"
              type="submit"
              data-testid="save-overlay-button"
              isLoading={
                updateOverlayMutation.isPending ||
                uploadUrlDataMutation.isPending
              }
              startIcon={<FloppyDiskIcon />}
            >
              Save Changes
            </Button>,
          ]}
        />
      </OverlayForm>
    </LoadingContainer>
  );
};
